import React from 'react'
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import {
    Box, useTheme, Typography, TextField, MenuItem,
    Collapse, CircularProgress, Alert as MuiAlert,
} from "@mui/material";
import { tokens } from "../../theme";
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Compact, accurate display for a set of pages: collapses consecutive runs but keeps
// gaps explicit, e.g. [3,4,5] -> "3–5", [3,9] -> "3, 9", [3,4,5,9] -> "3–5, 9", [7] -> "7".
const formatPageRange = (pages) => {
    if (!pages || pages.length === 0) return "";
    const sorted = [...new Set(pages)].sort((a, b) => a - b);
    const runs = [];
    let start = sorted[0];
    let prev = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === prev + 1) {
            prev = sorted[i];
        } else {
            runs.push(start === prev ? `${start}` : `${start}–${prev}`);
            start = sorted[i];
            prev = sorted[i];
        }
    }
    runs.push(start === prev ? `${start}` : `${start}–${prev}`);
    return runs.join(", ");
};

/**
 * Copy hw_questions from another textbook (source) into the current textbook (target),
 * scoped by a page range. Calls POST /api/v1/hw-questions/copy.
 */
const CopyQuestionsModal = ({
    targetTextbookId,
    targetTextbookTitle,
    setSuccessAlertOpen,
    setErrorAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertMessage,
    setUpdated,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [open, setOpen] = React.useState(false);
    const [textbooks, setTextbooks] = React.useState([]);
    const [loadingTextbooks, setLoadingTextbooks] = React.useState(false);

    const [sourceId, setSourceId] = React.useState("");
    const [pageFrom, setPageFrom] = React.useState("");
    const [pageTo, setPageTo] = React.useState("");
    const [pageOffset, setPageOffset] = React.useState("0");
    const [showAdvanced, setShowAdvanced] = React.useState(false);

    // Whether this (target) textbook's questions are used by existing homework.
    const [homeworkUsage, setHomeworkUsage] = React.useState(null); // { linked_materials_count, homeworks_count }

    const [preview, setPreview] = React.useState(null); // { copied_count, would_delete_count, would_orphan_count, affected_homeworks_count }
    const [previewing, setPreviewing] = React.useState(false);
    const [copying, setCopying] = React.useState(false);

    // Bumped whenever the inputs change; lets an in-flight preview ignore its stale response.
    const previewReqId = React.useRef(0);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        // reset
        setSourceId("");
        setPageFrom("");
        setPageTo("");
        setPageOffset("0");
        setShowAdvanced(false);
        setPreview(null);
        setHomeworkUsage(null);
    };

    // Load textbooks for the source dropdown when opened
    React.useEffect(() => {
        if (!open) return;
        let ignore = false; // drop a late response if the modal closed / textbook changed
        setLoadingTextbooks(true);
        Axios.get(`${process.env.REACT_APP_URL}/api/v1/textbooks`, {
            headers: { Authorization: getAuthHeader() },
        }).then((res) => {
            if (ignore) return;
            // exclude the current (target) textbook
            setTextbooks((res.data || []).filter((tb) => tb.id !== Number(targetTextbookId)));
        }).catch(() => {
            if (ignore) return;
            setTextbooks([]);
        }).finally(() => {
            if (ignore) return;
            setLoadingTextbooks(false);
        });
        return () => { ignore = true; };
    }, [open, targetTextbookId]);

    // Check whether THIS (target) textbook is already used by existing homework, so we
    // can warn that replacing its questions will affect that homework data.
    React.useEffect(() => {
        if (!open) return;
        let ignore = false; // drop a late response so a stale banner can't describe the wrong textbook
        Axios.get(`${process.env.REACT_APP_URL}/api/v1/hw-questions/textbook-usage/${targetTextbookId}`, {
            headers: { Authorization: getAuthHeader() },
        }).then((res) => {
            if (ignore) return;
            setHomeworkUsage(res.data || null);
        }).catch(() => {
            if (ignore) return;
            setHomeworkUsage(null);
        });
        return () => { ignore = true; };
    }, [open, targetTextbookId]);

    // Reset the preview whenever anything that affects the outcome changes.
    // Bumping previewReqId invalidates any preview request still in flight for the old inputs.
    React.useEffect(() => {
        previewReqId.current += 1;
        setPreview(null);
    }, [sourceId, pageFrom, pageTo, pageOffset]);

    const rangeValid =
        sourceId &&
        pageFrom !== "" && pageTo !== "" &&
        Number(pageFrom) <= Number(pageTo);

    // Build the request body shared by preview (dry-run) and the real copy
    const buildBody = (extra = {}) => ({
        source_textbook_id: Number(sourceId),
        target_textbook_id: Number(targetTextbookId),
        page_from: Number(pageFrom),
        page_to: Number(pageTo),
        page_offset: Number(pageOffset) || 0,
        conflict_strategy: "replace", // questions on the destination pages are always replaced
        ...extra,
    });

    const handlePreview = async () => {
        if (!rangeValid) return;
        const reqId = previewReqId.current; // inputs at the time this preview was requested
        setPreviewing(true);
        try {
            const res = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/hw-questions/copy`,
                buildBody({ dry_run: true }),
                { headers: { Authorization: getAuthHeader() } }
            );
            if (reqId !== previewReqId.current) return; // inputs changed mid-flight — discard stale result
            setPreview(res.data || null);
        } catch (error) {
            if (reqId !== previewReqId.current) return;
            setErrorAlertMessage(error.response?.data?.error || "Error: Cannot preview. Please try again.");
            setErrorAlertOpen(true);
            setPreview(null);
        } finally {
            // Only one preview is ever in flight (the button is disabled while previewing),
            // so always clear the spinner regardless of whether the result was discarded.
            setPreviewing(false);
        }
    };

    const handleCopy = async () => {
        if (!rangeValid) return;
        setCopying(true);
        try {
            const res = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/hw-questions/copy`,
                buildBody(),
                { headers: { Authorization: getAuthHeader() } }
            );

            const { copied_count, warnings = [] } = res.data || {};
            let msg = `${copied_count} question(s) copied successfully!`;
            if (warnings.length > 0) msg += ` (${warnings.join(" ")})`;
            setSuccessAlertMessage(msg);
            setSuccessAlertOpen(true);
            setUpdated(true);
            handleClose();
        } catch (error) {
            setErrorAlertMessage(error.response?.data?.error || "Error: Cannot copy questions. Please try again.");
            setErrorAlertOpen(true);
        } finally {
            setCopying(false);
        }
    };

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 560,
        maxHeight: "85vh",
        overflowY: "auto",
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        borderRadius: '8px',
    };

    return (
        <div style={{ display: 'inline-block' }}>
            <Button
                type="button"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleOpen}
                sx={{ ml: 2 }}
            >
                Copy From Another Textbook
            </Button>
            <Modal
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{ backdrop: { timeout: 500 } }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Typography variant="h6" fontWeight="bold" color={colors.primary[450]}>
                            Copy Questions Into This Textbook
                        </Typography>
                        <Typography variant="body2" color={colors.primary[300]} mt={1}>
                            Copy homework questions from another textbook into{" "}
                            <strong>{targetTextbookTitle || "this textbook"}</strong>. Existing
                            questions on the destination pages are replaced.
                        </Typography>

                        {/* This textbook is already used by homework — replacing its questions affects that data */}
                        {homeworkUsage && homeworkUsage.homeworks_count > 0 && (
                            <MuiAlert severity="error" sx={{ mt: 2 }}>
                                This textbook is used by {homeworkUsage.homeworks_count} existing homework
                                assignment(s)
                                {homeworkUsage.linked_materials_count > 0
                                    ? ` across ${homeworkUsage.linked_materials_count} class material(s)`
                                    : ""}
                                . Replacing questions on pages those homeworks reference will change or break
                                existing homework data.
                            </MuiAlert>
                        )}

                        <Box display="flex" flexDirection="column" gap={2.5} width="100%" pt={3}>
                            {/* Source textbook */}
                            <TextField
                                select
                                label="Source textbook"
                                value={sourceId}
                                onChange={(e) => setSourceId(e.target.value)}
                                fullWidth
                                disabled={loadingTextbooks}
                                helperText={loadingTextbooks ? "Loading textbooks..." : "Copy questions FROM this textbook"}
                            >
                                {textbooks.map((tb) => (
                                    <MenuItem key={tb.id} value={tb.id}>
                                        {tb.title}{tb.pages ? ` (${tb.pages}p)` : ""}
                                    </MenuItem>
                                ))}
                            </TextField>

                            {/* Page range */}
                            <Box display="flex" gap={2}>
                                <TextField
                                    label="Page from"
                                    type="number"
                                    value={pageFrom}
                                    onChange={(e) => setPageFrom(e.target.value)}
                                    fullWidth
                                    inputProps={{ min: 1 }}
                                />
                                <TextField
                                    label="Page to"
                                    type="number"
                                    value={pageTo}
                                    onChange={(e) => setPageTo(e.target.value)}
                                    fullWidth
                                    inputProps={{ min: 1 }}
                                />
                            </Box>
                            {pageFrom !== "" && pageTo !== "" && Number(pageFrom) > Number(pageTo) && (
                                <Typography variant="caption" color="error">
                                    "Page from" must be less than or equal to "Page to".
                                </Typography>
                            )}

                            {/* Advanced: page offset */}
                            <Box>
                                <Button size="small" onClick={() => setShowAdvanced((v) => !v)} sx={{ pl: 0 }}>
                                    {showAdvanced ? "Hide" : "Show"} advanced options
                                </Button>
                                <Collapse in={showAdvanced}>
                                    <TextField
                                        label="Page offset"
                                        type="number"
                                        value={pageOffset}
                                        onChange={(e) => setPageOffset(e.target.value)}
                                        helperText="target page = source page + offset. Use 0 if pages line up 1:1; use a negative value if the source is numbered higher (e.g. source p.10 → target p.1 is -9). Default 0."
                                        sx={{ mt: 1, width: 240 }}
                                    />
                                </Collapse>
                            </Box>

                            {/* Preview */}
                            {preview !== null && (
                                <MuiAlert
                                    severity={preview.copied_count > 0 ? (preview.would_delete_count > 0 ? "warning" : "info") : "warning"}
                                    sx={{ py: 0.5 }}
                                >
                                    {preview.copied_count > 0
                                        ? `${preview.copied_count} question(s) will be copied.`
                                        : "No questions found in that page range."}
                                    {preview.would_delete_count > 0 && (
                                        <Box component="span" sx={{ display: 'block', fontWeight: 'bold', mt: 0.5 }}>
                                            ⚠ {preview.would_delete_count} existing question(s) on the destination pages will be DELETED first.
                                        </Box>
                                    )}
                                    {preview.would_orphan_count > 0 && (
                                        <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                                            {preview.would_orphan_count} other question(s) will lose their parent link.
                                        </Box>
                                    )}
                                </MuiAlert>
                            )}

                            {/* Red warning: destination pages feed existing homework */}
                            {preview !== null && preview.affected_homeworks_count > 0 && (
                                <MuiAlert severity="error" sx={{ py: 0.5 }}>
                                    ⚠ {preview.affected_homeworks_count} existing homework assignment(s) reference
                                    the destination page(s)
                                    {preview.destination_pages?.length
                                        ? ` (p. ${formatPageRange(preview.destination_pages)})`
                                        : ""}
                                    . Replacing these questions will affect that homework data.
                                </MuiAlert>
                            )}

                            {/* Actions */}
                            <Box display="flex" gap={2} mt={1}>
                                <Button
                                    variant="outlined"
                                    onClick={handlePreview}
                                    disabled={!rangeValid || previewing}
                                >
                                    {previewing ? <CircularProgress size={18} /> : "Preview"}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleCopy}
                                    disabled={
                                        !rangeValid ||
                                        copying ||
                                        (preview && preview.copied_count === 0) ||
                                        // replacing deletes existing target data — force a preview first
                                        preview === null
                                    }
                                >
                                    {copying ? "Copying..." : "Copy Questions"}
                                </Button>
                                {preview === null && (
                                    <Typography variant="caption" color={colors.primary[300]} alignSelf="center">
                                        Preview required before replacing.
                                    </Typography>
                                )}
                                <Button variant="text" onClick={handleClose} disabled={copying}>
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </div>
    )
}

export default CopyQuestionsModal
